import React, { useState, useEffect, useCallback } from 'react';
import ApiClient from '../../../methods/api/apiClient';

const MarketRateSuggestion = ({ originZip, destinationZip, onRateSelect }) => {
  const [rateData, setRateData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [collapsed, setCollapsed] = useState(false);

  const fetchRate = useCallback(async () => {
    if (!originZip || !destinationZip || originZip.length < 3 || destinationZip.length < 3) {
      setRateData(null);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const res = await ApiClient.get(
        `rates?origin_location_postal_code=${originZip}&destination_location_postal_code=${destinationZip}`
      );

      if (res?.success && res?.data?.length > 0) {
        setRateData(res.data[0]); // Take the first/best rate
      } else {
        setRateData(null);
        if (res?.success) setError('No rate data for this lane');
      }
    } catch (err) {
      setError('Rate lookup failed');
    }
    setLoading(false);
  }, [originZip, destinationZip]);

  // Debounce: fetch rate 800ms after both zips are available
  useEffect(() => {
    const timer = setTimeout(() => {
      fetchRate();
    }, 800);
    return () => clearTimeout(timer);
  }, [originZip, destinationZip, fetchRate]);

  if (!originZip || !destinationZip || originZip.length < 3 || destinationZip.length < 3) {
    return null;
  }

  // Confidence indicator
  const getConfidenceInfo = (score) => {
    if (score >= 4) return { label: 'High', color: '#27ae60', bg: '#e8f8f0' };
    if (score >= 2) return { label: 'Medium', color: '#f39c12', bg: '#fef9e7' };
    return { label: 'Low', color: '#e74c3c', bg: '#fdedec' };
  };

  return (
    <div style={{
      border: '1px solid #d4e6f1',
      borderRadius: 8,
      padding: collapsed ? '8px 12px' : '12px 16px',
      backgroundColor: '#eaf2f8',
      marginBottom: 16,
      transition: 'all 0.2s ease'
    }}>
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        cursor: 'pointer'
      }} onClick={() => setCollapsed(!collapsed)}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{
            background: '#3E549D',
            color: '#fff',
            padding: '2px 8px',
            borderRadius: 4,
            fontSize: 11,
            fontWeight: 600
          }}>MARKET RATE</span>
          {loading && <span style={{ fontSize: 12, color: '#666' }}>Loading...</span>}
          {rateData && !loading && (
            <span style={{ fontSize: 16, fontWeight: 700, color: '#2c3e50' }}>
              ${rateData.rate?.toLocaleString()}
              <span style={{ fontSize: 12, fontWeight: 400, color: '#7f8c8d', marginLeft: 4 }}>
                ({rateData.rpm?.toFixed(2)}/mi)
              </span>
            </span>
          )}
          {error && !loading && (
            <span style={{ fontSize: 12, color: '#e74c3c' }}>{error}</span>
          )}
        </div>
        <i className={`fa fa-chevron-${collapsed ? 'down' : 'up'}`} style={{ fontSize: 12, color: '#666' }}></i>
      </div>

      {!collapsed && rateData && !loading && (
        <div style={{ marginTop: 10 }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, fontSize: 13 }}>
            <div>
              <span style={{ color: '#7f8c8d' }}>Range: </span>
              <strong>${rateData.rate_low} - ${rateData.rate_high}</strong>
            </div>
            <div>
              <span style={{ color: '#7f8c8d' }}>RPM: </span>
              <strong>{rateData.rpm_low?.toFixed(2)} - {rateData.rpm_high?.toFixed(2)}</strong>
            </div>
            <div>
              <span style={{ color: '#7f8c8d' }}>Distance: </span>
              <strong>{rateData.length_of_haul} mi</strong>
            </div>
            {rateData.confidence_score && (() => {
              const conf = getConfidenceInfo(rateData.confidence_score);
              return (
                <div style={{
                  background: conf.bg,
                  color: conf.color,
                  padding: '1px 8px',
                  borderRadius: 4,
                  fontWeight: 600,
                  fontSize: 12
                }}>
                  Confidence: {conf.label} ({rateData.confidence_score}/5)
                </div>
              );
            })()}
            <div>
              <span style={{ color: '#7f8c8d' }}>Loads: </span>
              <strong>{rateData.total_load_count}</strong>
            </div>
          </div>

          {onRateSelect && (
            <div style={{ marginTop: 8, display: 'flex', gap: 8 }}>
              <button
                type="button"
                className="btn btn-sm btn-outline-primary"
                style={{ fontSize: 11, padding: '2px 10px' }}
                onClick={() => onRateSelect(rateData.rate)}
              >
                Use ${rateData.rate}
              </button>
              <button
                type="button"
                className="btn btn-sm btn-outline-secondary"
                style={{ fontSize: 11, padding: '2px 10px' }}
                onClick={() => onRateSelect(rateData.rate_low)}
              >
                Use Low ${rateData.rate_low}
              </button>
              <button
                type="button"
                className="btn btn-sm btn-outline-secondary"
                style={{ fontSize: 11, padding: '2px 10px' }}
                onClick={() => onRateSelect(rateData.rate_high)}
              >
                Use High ${rateData.rate_high}
              </button>
            </div>
          )}

          <div style={{ marginTop: 6, fontSize: 11, color: '#95a5a6' }}>
            <i className="fa fa-clock-o" style={{ marginRight: 4 }}></i>
            Data: {rateData.data_timestamp || 'N/A'} | Equipment: {rateData.equipment_type || 'VAN'}
            <button
              type="button"
              style={{
                border: 'none',
                background: 'none',
                color: '#3E549D',
                cursor: 'pointer',
                fontSize: 11,
                marginLeft: 8,
                textDecoration: 'underline'
              }}
              onClick={(e) => {
                e.stopPropagation();
                fetchRate();
              }}
            >
              Refresh
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default MarketRateSuggestion;
